import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["defaultValue", "disableResultsNavigation", "hideQuickLinks", "removeSearchQueryOnClear", "onAutocompletePopularContentSelected", "onAutocompleteSuggestionSelected", "onClearSearch", "onSearch", "onSearchEnter", "onSearchValueChange", "onSearchClick"];
import I18n from 'I18n';
import { SearchInput } from 'foundations-components/transitional/components/SearchInput';
import { useState, useRef, useEffect } from 'react';
import { SearchAutocompleteResults, getOptionsTitleId } from './SearchAutocompleteResults';
import { useHistory, useLocation } from 'quartz-routing/react-router';
import { makeInlineContentCardUrl } from './SearchAutocompleteResults';
import { useGetQuery } from '../../../__generated__/chirp/com/hubspot/learning/center/search/rpc/AutocompleteChirpService.hooks';
import getLang from 'academy-common-lib/utils/getLang';
import useDebouncedValue from 'academy-common-lib/hooks/useDebouncedValue';
import useSearchParams from 'academy-common-lib/hooks/useSearchParams';
import { getOptionId } from './SearchAutocompleteResultItem';
import { View } from 'foundations-components/components/View';
import { useNavigateToResultsPage } from './hooks/useNavigateToResultsPage';
import makeSuggestions from './utils/makeSuggestions';
import { useEffectEvent } from 'academy-common-lib/hooks/useEffectEvent';
import { useRemoveSearchQuery } from './hooks/useRemoveSearchQuery';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const MIN_SEARCH_LENGTH = 2;
const QUICK_LINKS_LIMIT = 3;
const POPULAR_TERMS_LIMIT = 6;
const DEBOUNCE_DELAY = 400;
export const ACSearchInput = _ref => {
  var _ref2;
  let {
      defaultValue,
      disableResultsNavigation = false,
      hideQuickLinks = false,
      removeSearchQueryOnClear = false,
      onAutocompletePopularContentSelected,
      onAutocompleteSuggestionSelected,
      onClearSearch,
      onSearch,
      onSearchEnter,
      onSearchValueChange = () => {},
      onSearchClick
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.search;
  const [searchValue, setSearchValue] = useState((_ref2 = defaultValue !== null && defaultValue !== void 0 ? defaultValue : searchQuery) !== null && _ref2 !== void 0 ? _ref2 : '');
  const debouncedSearchValue = useDebouncedValue(searchValue, DEBOUNCE_DELAY);
  const onSearchValueChangeEvent = useEffectEvent(onSearchValueChange);
  useEffect(() => {
    onSearchValueChangeEvent(debouncedSearchValue);
  }, [debouncedSearchValue]);
  const {
    data: suggestionsData
  } = useGetQuery({
    skip: debouncedSearchValue.length < MIN_SEARCH_LENGTH,
    variables: {
      queryTerm: debouncedSearchValue,
      language: getLang().toUpperCase(),
      popularTermsLimit: POPULAR_TERMS_LIMIT,
      quickLinksLimit: QUICK_LINKS_LIMIT
    }
  });
  const [suggestions, setSuggestions] = useState(() => makeSuggestions({
    suggestionsData,
    hideQuickLinks
  }));
  const [prevSuggestionsData, setPrevSuggestionsData] = useState(suggestionsData);
  if (prevSuggestionsData !== suggestionsData) {
    setPrevSuggestionsData(suggestionsData);
    if (suggestionsData) {
      const newSuggestions = makeSuggestions({
        suggestionsData,
        hideQuickLinks
      });
      const hasResults = Object.values(newSuggestions).some(items => items.length > 0);
      if (hasResults) {
        setSuggestions(newSuggestions);
      } else {
        setSuggestions(undefined);
      }
    }
  }
  const removeSearchQuery = useRemoveSearchQuery();
  const handleSearchValueChange = newSearchValue => {
    setSearchValue(newSearchValue);
    if (newSearchValue.length < MIN_SEARCH_LENGTH) {
      setSuggestions(undefined);
    }
    if (newSearchValue.length === 0) {
      if (removeSearchQueryOnClear) {
        removeSearchQuery();
      }
      onClearSearch === null || onClearSearch === void 0 || onClearSearch();
    }
  };
  const handleSearchValueChangeEvent = useEffectEvent(handleSearchValueChange);
  const isFirstRenderRef = useRef(true);
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      if (searchQuery) {
        handleSearchValueChangeEvent(searchQuery);
      }
      return;
    }
    handleSearchValueChangeEvent(searchQuery !== null && searchQuery !== void 0 ? searchQuery : '');
  }, [searchQuery]);
  const inputRef = useRef(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const blurInput = () => {
    var _inputRef$current;
    setIsInputFocused(false);
    (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 || _inputRef$current.blur();
  };
  const handleHeaderClick = searchTerm => {
    onSearchClick === null || onSearchClick === void 0 || onSearchClick(searchTerm);
    onSearch === null || onSearch === void 0 || onSearch(searchTerm);
    blurInput();
  };
  const [highlightedOptionIndex, setHighlightedOptionIndex] = useState(null);
  const history = useHistory();
  const location = useLocation();
  const navigateToResultsPage = useNavigateToResultsPage();
  const handleKeyDown = e => {
    if (e.key === 'Escape') {
      setSearchValue('');
      return;
    }
    const submitSearch = term => {
      if (!disableResultsNavigation) {
        navigateToResultsPage({
          search: term
        });
      }
      onSearch === null || onSearch === void 0 || onSearch(term);
    };
    if (!suggestions) {
      if (e.key === 'Enter' && searchValue.length > 0) {
        submitSearch(searchValue);
        onSearchEnter === null || onSearchEnter === void 0 || onSearchEnter(searchValue);
        blurInput();
      }
      return;
    }
    const getTitleIndices = () => Object.entries(suggestions).reduce((acc, [key, items]) => {
      acc.indices[key] = acc.totalOptions;
      acc.totalOptions += items.length;
      return acc;
    }, {
      indices: {},
      totalOptions: 0
    }).indices;
    const scrollIntoView = optionIndex => {
      const option = document.getElementById(getOptionId(optionIndex));
      option === null || option === void 0 || option.scrollIntoView({
        block: 'nearest',
        inline: 'nearest'
      });
      const titleIndex = Object.values(getTitleIndices()).find(index => index === optionIndex);
      if (titleIndex !== undefined) {
        const title = document.getElementById(getOptionsTitleId(titleIndex));
        title === null || title === void 0 || title.scrollIntoView({
          block: 'nearest',
          inline: 'nearest'
        });
      }
    };
    const totalOptions = Object.values(suggestions).reduce((acc, items) => acc + items.length, 0);
    switch (e.key) {
      case 'ArrowUp':
        {
          e.preventDefault();
          const newHighlightedOptionIndex = highlightedOptionIndex === null || highlightedOptionIndex <= 0 ? totalOptions - 1 : highlightedOptionIndex - 1;
          setHighlightedOptionIndex(newHighlightedOptionIndex);
          scrollIntoView(newHighlightedOptionIndex);
          break;
        }
      case 'ArrowDown':
        {
          e.preventDefault();
          const newHighlightedOptionIndex = highlightedOptionIndex === null || highlightedOptionIndex >= totalOptions - 1 ? 0 : highlightedOptionIndex + 1;
          setHighlightedOptionIndex(newHighlightedOptionIndex);
          scrollIntoView(newHighlightedOptionIndex);
          break;
        }
      case 'Enter':
        {
          e.preventDefault();
          if (highlightedOptionIndex !== null) {
            const titleIndices = getTitleIndices();
            if (highlightedOptionIndex >= titleIndices.popularTerms && highlightedOptionIndex < titleIndices.popularTerms + suggestions.popularTerms.length) {
              const highlightedOption = suggestions.popularTerms[highlightedOptionIndex - titleIndices.popularTerms];
              handleSearchValueChange(highlightedOption.term);
              submitSearch(highlightedOption.term);
              onAutocompleteSuggestionSelected === null || onAutocompleteSuggestionSelected === void 0 || onAutocompleteSuggestionSelected(highlightedOption.term, highlightedOptionIndex);
            } else if (highlightedOptionIndex >= titleIndices.quickLinks && highlightedOptionIndex < titleIndices.quickLinks + suggestions.quickLinks.length) {
              const highlightedOption = suggestions.quickLinks[highlightedOptionIndex - titleIndices.quickLinks];
              handleSearchValueChange(highlightedOption.name);
              history.push(makeInlineContentCardUrl(highlightedOption, location));
              onAutocompletePopularContentSelected === null || onAutocompletePopularContentSelected === void 0 || onAutocompletePopularContentSelected(highlightedOption, highlightedOptionIndex);
            }
          } else if (searchValue.length > 0) {
            submitSearch(searchValue);
            onSearchEnter === null || onSearchEnter === void 0 || onSearchEnter(searchValue);
          }
          blurInput();
          break;
        }
      default:
        break;
    }
  };
  const handleResultItemClick = (option, index, result) => {
    var _option$text;
    handleSearchValueChange((_option$text = option.text) !== null && _option$text !== void 0 ? _option$text : '');
    if (result.type === 'popularTerms') {
      onSearch === null || onSearch === void 0 || onSearch(option.text);
      onAutocompleteSuggestionSelected === null || onAutocompleteSuggestionSelected === void 0 || onAutocompleteSuggestionSelected(option.text, index);
    } else if (result.type === 'quickLinks') {
      onAutocompletePopularContentSelected === null || onAutocompletePopularContentSelected === void 0 || onAutocompletePopularContentSelected(option, index);
    }
    blurInput();
  };

  // Blur fires before click, so clicking an autocomplete result would
  // unmount the dropdown before the click registers. To prevent this,
  // we track whether a mousedown happened inside the container. If so,
  // the blur handler skips dismissal and lets the click handler run first
  // (which calls blurInput itself).
  const isMouseDownInContainerRef = useRef(false);
  const handleBlur = () => {
    const isMouseDownInContainer = isMouseDownInContainerRef.current;
    if (!isMouseDownInContainer) {
      blurInput();
    }
  };
  const handleFocus = () => {
    setHighlightedOptionIndex(null);
    setIsInputFocused(true);
  };
  const handleChange = e => {
    setHighlightedOptionIndex(null);
    handleSearchValueChange(e.target.value);
  };
  const handleMouseUp = () => {
    isMouseDownInContainerRef.current = false;
  };
  const handleMouseDown = () => {
    isMouseDownInContainerRef.current = true;
  };
  return /*#__PURE__*/_jsxs(View, {
    position: "relative",
    onMouseDown: handleMouseDown,
    onMouseUp: handleMouseUp,
    children: [/*#__PURE__*/_jsx(SearchInput, Object.assign({
      "data-test-id": "filter-type-search",
      inputRef: inputRef,
      onChange: handleChange,
      onFocus: handleFocus,
      onBlur: handleBlur,
      onKeyDown: handleKeyDown,
      placeholder: I18n.text('academy-navigation-lib.ACPage.header.search.placeholder'),
      value: searchValue
    }, props)), isInputFocused && searchValue.length >= MIN_SEARCH_LENGTH && /*#__PURE__*/_jsx(SearchAutocompleteResults, {
      disableResultsNavigation: disableResultsNavigation,
      highlightedOptionIndex: highlightedOptionIndex,
      inputRef: inputRef,
      onResultItemMouseEnter: (__option, index) => {
        setHighlightedOptionIndex(index);
      },
      onResultItemClick: handleResultItemClick,
      searchValue: searchValue,
      suggestions: suggestions,
      onHeaderClick: handleHeaderClick
    })]
  });
};